<template>
  <div :class="responsiveClasses">
    <LFieldValidator
      v-if="!field.config.content_type"
      :id="field.config.field_id"
      :disabled="field.config.disabled === true"
      :model-value="field.config.value"
      :rules="validationRules"
    >
      <template #default="{ error, validate }">
        <!--
        TODO: Die props die über v-bind gesetzt werden, werden zusätzlich in der Komponente auch gesetzt.
              Dabei gewinnen die Werte die über v-bind runtergereicht werden.
              Beispiel ATextfieldMasked. Wenn in der ATextfieldMasked Komponente direkt eine maske an das
              LTextfield übergeben wird, wird dies durch den mask Wert der über v-bind reinkommt überschrieben.
              Heisst wir setzen die für das LTextfield quasi zweimal, einmal übers v-bind und einmal in
              der ATextfieldMask. Das ist ein architektonisches Problem und sollte zukünftig gelöst/bedacht werden
        -->
        <component
          :is="components[field.component]"
          v-bind="fieldProps"
          :error="error || fallbackError"
          :alias="$t(field.config.alias || '')"
          :placeholder="$t(field.config.placeholder || '')"
          :text="$t(field.config.text || '')"
          :info-message="$t(field.config.info_message || '')"
          :aria-label="$t(field.config.aria_label || '')"
          :disabled="hasApiError && variant === 'mini'"
          @update:model-value="handleComponentUpdate($event, validate)"
          @open-modal="$emit('open-modal', $event)"
        />
      </template>
    </LFieldValidator>

    <component
      :is="components[field.component]"
      v-else
      :element="field"
      :config="field.config"
      :step-id="field.config.step_id"
      :field-id="field.config.field_id"
    />
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, ref } from 'vue'
import type { FormField } from '@/ts/types/schema.types'
import { FieldRuleService } from '@/services/field-rule.service'
import { DynamicFormService } from '@/services/dynamic-form.service'
import { LFieldValidator } from '@wgg.hyve/linexo-form-validator'
import type { ValidationFn } from '@wgg.hyve/linexo-form-validator'
import { useGlobalStore } from '@/stores/globalStore'
import { storeToRefs } from 'pinia'
import { useDataAttributes } from '@/composables/dataAttributes'

type Props = {
  field: FormField
  components: Record<string, Component>
  state: { mounted: boolean }
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:model-value', payload: { stepId: number; fieldId: string; value: any }): void
  (e: 'open-modal', payload: any): void
}>()

const { hasApiError } = storeToRefs(useGlobalStore())
const { variant } = useDataAttributes()

const responsiveClasses = computed(() => DynamicFormService.getResponsiveClasses(props.field.config.cols))
const fieldProps = computed(() => DynamicFormService.getFieldProps(props.field))
const validationRules = computed<ValidationFn[]>(() => FieldRuleService.getRules(props.field.config.field_rules || []))
const hasUserInteracted = ref(false)
const fallbackError = computed(() => (hasUserInteracted.value ? '' : props.field.config.error || ''))

const handleFieldUpdate = (newValue: any) => {
  emit('update:model-value', {
    stepId: props.field.config.step_id,
    fieldId: props.field.config.field_id,
    value: newValue
  })
}

const handleComponentUpdate = (value: unknown, validateFn: (showErrorMessage?: boolean, stateful?: boolean) => Promise<boolean>) => {
  hasUserInteracted.value = true
  handleFieldUpdate(value)
  validateFn()
}
</script>
